// frontend/src/pages/Analytics/SummaryBorangAduan.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Typography, Row, Col, DatePicker, Button, Table, 
  Spin, Alert, Statistic, Space, Tag, Empty, message, Radio,
  Select, Form, Divider
} from 'antd';
import {
  FileExcelOutlined, FileWordOutlined, ReloadOutlined,
  CheckCircleOutlined, ClockCircleOutlined, FileTextOutlined,
  FilterOutlined, ClearOutlined, ShoppingOutlined, AppstoreOutlined,
  UnorderedListOutlined, WarningOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import api from '../../utils/api';
import './Analytics.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Constants for filter values
const ALL_CONTRACTS = 'all_contracts';
const ALL_TICKET_TYPES = 'all_ticket_types';

const SummaryBorangAduan = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const notificationApi = useNotificationApi();

  // State
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('docx');
  
  // Filter states
  const [contracts, setContracts] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [selectedContract, setSelectedContract] = useState(ALL_CONTRACTS); // Default to "All Contracts"
  const [selectedTicketType, setSelectedTicketType] = useState(ALL_TICKET_TYPES); // Default to "All Ticket Types"
  const [loadingFilters, setLoadingFilters] = useState(false);
  
  // Track if filters have been applied
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Fetch contracts and ticket types on component mount
  useEffect(() => {
    fetchContracts();
    fetchTicketTypes();
  }, []);

  const showError = useCallback((title, content, key = 'summaryError') => {
    notificationApi.error({
      key,
      message: title,
      description: content,
      duration: 4.5,
      placement: 'topRight',
    });
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'summarySuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
    });
  }, [notificationApi]);

  const fetchContracts = async () => {
    setLoadingFilters(true);
    try {
      const response = await api.get('/api/contracts', {
        params: {
          status: 'active',
          sortBy: 'contractName:ASC'
        }
      });
      
      if (response.data.success) {
        setContracts(response.data.data || []);
        console.log('[DEBUG] Contracts loaded:', response.data.data.length);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
      showError('Failed to Load Contracts', 'Unable to load contract list for filtering.');
    } finally {
      setLoadingFilters(false);
    }
  };

  const fetchTicketTypes = async () => {
    try {
      const response = await api.get('/api/ticket-types', {
        params: {
          isActive: true
        }
      });
      
      if (response.data.success) {
        setTicketTypes(response.data.data || []);
        console.log('[DEBUG] Ticket types loaded:', response.data.data.length);
      }
    } catch (error) {
      console.error('Error fetching ticket types:', error);
    }
  };

  // Get the actual contract ID to send to API (null means no filter)
  const getContractIdForApi = () => {
    if (selectedContract === ALL_CONTRACTS) return null;
    return selectedContract;
  };

  // Get the actual ticket type ID to send to API (null means no filter)
  const getTicketTypeIdForApi = () => {
    if (selectedTicketType === ALL_TICKET_TYPES) return null;
    return selectedTicketType;
  };

  // Apply filters and fetch data
  const handleApplyFilters = async () => {
    if (!dateRange || dateRange.length !== 2) {
      showError('Validation Error', 'Please select a date range first.');
      return;
    }
    
    setLoading(true);
    setFiltersApplied(true);
    
    try {
      const startMonth = dateRange[0].format('YYYY-MM');
      const endMonth = dateRange[1].format('YYYY-MM');
      
      const params = {
        startMonth: startMonth,
        endMonth: endMonth
      };
      
      // Only add contract filter if NOT "All Contracts"
      const contractId = getContractIdForApi();
      if (contractId) {
        params.contractId = contractId;
      }
      
      // Only add ticket type filter if NOT "All Ticket Types"
      const ticketTypeId = getTicketTypeIdForApi();
      if (ticketTypeId) {
        params.ticketTypeId = ticketTypeId;
      }
      
      const response = await api.get('/api/reports/summary-borang-aduan/data', { params });
      
      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        showError('Failed to Load Report', response.data.message || 'Unable to load report data');
        setReportData(null);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      showError('Report Error', error.response?.data?.message || 'Failed to generate report');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDocument = async () => {
    if (!reportData || !reportData.tickets || reportData.tickets.length === 0) {
      showError('Export Failed', 'No data to export. Please apply filters first.');
      return;
    }
    
    if (!dateRange || dateRange.length !== 2) {
      showError('Export Failed', 'Please select a valid date range.');
      return;
    }
    
    setExportLoading(true);
    try {
      const startMonth = dateRange[0].format('YYYY-MM');
      const endMonth = dateRange[1].format('YYYY-MM');
      
      const params = {
        startMonth: startMonth,
        endMonth: endMonth,
        format: selectedFormat
      };
      
      const contractId = getContractIdForApi();
      if (contractId) {
        params.contractId = contractId;
      }
      
      const ticketTypeId = getTicketTypeIdForApi();
      if (ticketTypeId) {
        params.ticketTypeId = ticketTypeId;
      }
      
      const response = await api.get('/api/reports/summary-borang-aduan', {
        params: params,
        responseType: 'blob'
      });
      
      // Build filename
      let filename = `Summary_Borang_Aduan_${startMonth}`;
      if (startMonth !== endMonth) {
        filename += `_to_${endMonth}`;
      }
      
      // Add contract name to filename if specific contract selected
      if (selectedContract !== ALL_CONTRACTS && reportData?.contractName && reportData.contractName !== 'All Contracts') {
        const safeContractName = reportData.contractName.replace(/[^a-zA-Z0-9]/g, '_');
        filename += `_${safeContractName}`;
      }
      
      // Add ticket type to filename if specific type selected
      if (selectedTicketType !== ALL_TICKET_TYPES && reportData?.ticketTypeFilter && reportData.ticketTypeFilter !== 'All Types') {
        const safeTypeName = reportData.ticketTypeFilter.replace(/[^a-zA-Z0-9]/g, '_');
        filename += `_${safeTypeName}`;
      }
      
      filename += selectedFormat === 'pdf' ? '.pdf' : '.docx';
      
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      const formatName = selectedFormat === 'pdf' ? 'PDF' : 'Word';
      showSuccess('Export Successful', `Report exported to ${formatName} successfully.`);
    } catch (error) {
      console.error('Export error:', error);
      showError('Export Failed', error.response?.data?.message || 'Failed to generate document');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // Clear results when date range changes
    setReportData(null);
    setFiltersApplied(false);
  };

  const handleClearFilters = () => {
    setDateRange(null);
    setSelectedContract(ALL_CONTRACTS);
    setSelectedTicketType(ALL_TICKET_TYPES);
    setReportData(null);
    setFiltersApplied(false);
  };

  const handleFormatChange = (e) => {
    setSelectedFormat(e.target.value);
  };

  const handleContractChange = (value) => {
    setSelectedContract(value);
    // Clear results when filter changes
    setReportData(null);
    setFiltersApplied(false);
  };

  const handleTicketTypeChange = (value) => {
    setSelectedTicketType(value);
    // Clear results when filter changes
    setReportData(null);
    setFiltersApplied(false);
  };

  // Get display name for selected contract
  const getContractDisplayName = () => {
    if (selectedContract === ALL_CONTRACTS) return 'All Contracts';
    const contract = contracts.find(c => c.id === parseInt(selectedContract));
    return contract ? contract.contractName : 'All Contracts';
  };

  // Get display name for selected ticket type
  const getTicketTypeDisplayName = () => {
    if (selectedTicketType === ALL_TICKET_TYPES) return 'All Types';
    const type = ticketTypes.find(t => t.id === parseInt(selectedTicketType));
    return type ? type.name : 'All Types';
  };

  // Check if any specific filter is selected (not "All")
  const hasSpecificFilters = () => {
    return selectedContract !== ALL_CONTRACTS || selectedTicketType !== ALL_TICKET_TYPES;
  };

  const disabledDate = (current) => {
    return current && current > dayjs().endOf('month');
  };

  const getTableColumns = () => {
    return [
      {
        title: 'Ticket Number',
        dataIndex: 'ticket_number',
        key: 'ticket_number',
        width: 180,
        fixed: 'left',
        render: (text) => (
          <Text code copyable={{ text }}>
            {text}
          </Text>
        )
      },
      {
        title: 'Products',
        dataIndex: 'products',
        key: 'products',
        width: 150,
        ellipsis: true
      },
      {
        title: 'Activity Type',
        dataIndex: 'activity_type',
        key: 'activity_type',
        width: 130,
        render: (text) => <Tag color="blue">{text}</Tag>
      },
      {
        title: 'Severity',
        dataIndex: 'severity',
        key: 'severity',
        width: 100,
        render: (severity) => {
          let color = 'default';
          if (severity === 'High') color = 'red';
          else if (severity === 'Medium') color = 'orange';
          else if (severity === 'Low') color = 'green';
          return <Tag color={color}>{severity}</Tag>;
        }
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 250,
        ellipsis: true,
        render: (text) => (
          <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 200 }}>
            {text}
          </Text>
        )
      },
      {
        title: 'Open Date',
        dataIndex: 'open_date',
        key: 'open_date',
        width: 110,
        align: 'center'
      },
      {
        title: 'Close Date',
        dataIndex: 'close_date',
        key: 'close_date',
        width: 110,
        align: 'center',
        render: (date) => date !== 'Masih Aktif' ? date : <Tag color="orange">{date}</Tag>
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        align: 'center',
        render: (status) => <Tag color="green">{status}</Tag>
      }
    ];
  };

  const getTableDataSource = () => {
    if (!reportData?.tickets) return [];
    return reportData.tickets.map((ticket, index) => ({
      ...ticket,
      key: ticket.ticket_number || index
    }));
  };

  const getDateRangeDisplay = () => {
    if (!dateRange || dateRange.length !== 2) return '-';
    const start = dateRange[0].format('MMMM YYYY');
    const end = dateRange[1].format('MMMM YYYY');
    if (start === end) return start;
    return `${start} - ${end}`;
  };

  const getMonthCount = () => {
    if (!dateRange || dateRange.length !== 2) return 0;
    const start = dateRange[0];
    const end = dateRange[1];
    const months = (end.year() - start.year()) * 12 + (end.month() - start.month()) + 1;
    return months;
  };

  const getOrganizationName = () => {
    return user?.organizationName || "YongPeng Solutions Sdn. Bhd.";
  };

  const renderTicketTypeSummary = () => {
    if (!reportData?.ticketTypeSummary) return null;
    
    const summary = reportData.ticketTypeSummary;
    const summaryItems = Object.entries(summary);
    
    if (summaryItems.length === 0) return null;
    
    return (
      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <Divider orientation="left">
          <Space>
            <UnorderedListOutlined />
            <span>Ticket Type Summary</span>
          </Space>
        </Divider>
        <Row gutter={[16, 16]}>
          {summaryItems.map(([type, count]) => (
            <Col key={type} xs={12} sm={8} md={6} lg={4}>
              <Card size="small" className="summary-type-card" style={{ textAlign: 'center' }}>
                <Statistic
                  title={type}
                  value={count}
                  valueStyle={{ fontSize: 24, color: '#1890ff' }}
                  prefix={<AppstoreOutlined />}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <Title level={2} className="analytics-title">Summary Borang Aduan</Title>
        <Text className="analytics-description">
          Generate summary complaint reports for closed tickets. Select date range, optionally filter by contract and/or ticket type, then click Apply.
        </Text>
      </div>

      <Card className="filter-card">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">
              <Text strong>Date Range (Month/Year) <span style={{ color: 'red' }}>*</span></Text>
            </div>
            <RangePicker
              picker="month"
              value={dateRange}
              onChange={handleDateRangeChange}
              format="MMM YYYY"
              style={{ width: '100%' }}
              placeholder={['Start Month', 'End Month']}
              disabledDate={disabledDate}
              allowClear={true}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Required - Select month range
            </Text>
          </Col>
          
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">
              <Text strong><ShoppingOutlined /> Contract</Text>
            </div>
            <Select
              value={selectedContract}
              onChange={handleContractChange}
              style={{ width: '100%' }}
              loading={loadingFilters}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value={ALL_CONTRACTS}>📋 All Contracts</Option>
              {contracts.map(contract => (
                <Option key={contract.id} value={contract.id}>
                  📄 {contract.contractName}
                </Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Select "All Contracts" or a specific contract
            </Text>
          </Col>
          
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">
              <Text strong><AppstoreOutlined /> Ticket Type</Text>
            </div>
            <Select
              value={selectedTicketType}
              onChange={handleTicketTypeChange}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value={ALL_TICKET_TYPES}>🎫 All Ticket Types</Option>
              {ticketTypes.map(type => (
                <Option key={type.id} value={type.id}>
                  📌 {type.name}
                </Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Select "All Ticket Types" or a specific type
            </Text>
          </Col>
          
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">
              <Text strong>Output Format</Text>
            </div>
            <Radio.Group 
              value={selectedFormat} 
              onChange={handleFormatChange}
              buttonStyle="solid"
              style={{ width: '100%' }}
            >
              <Radio.Button value="docx" style={{ width: '50%', textAlign: 'center' }}>
                <FileWordOutlined /> DOCX
              </Radio.Button>
              <Radio.Button value="pdf" style={{ width: '50%', textAlign: 'center' }}>
                <FileExcelOutlined /> PDF
              </Radio.Button>
            </Radio.Group>
          </Col>
        </Row>
        
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} sm={8} md={6} lg={4}>
            <Button 
              icon={<ClearOutlined />} 
              onClick={handleClearFilters}
              style={{ width: '100%' }}
            >
              Clear All
            </Button>
          </Col>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Button 
              type="primary"
              icon={<FilterOutlined />} 
              onClick={handleApplyFilters}
              style={{ width: '100%' }}
              disabled={!dateRange || dateRange.length !== 2}
              size="large"
            >
              Apply Filters & Generate Report
            </Button>
          </Col>
        </Row>
        
        {hasSpecificFilters() && (
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Active Filters: </Text>
            {selectedContract !== ALL_CONTRACTS && (
              <Tag color="blue" closable onClose={() => setSelectedContract(ALL_CONTRACTS)}>
                Contract: {getContractDisplayName()}
              </Tag>
            )}
            {selectedTicketType !== ALL_TICKET_TYPES && (
              <Tag color="cyan" closable onClose={() => setSelectedTicketType(ALL_TICKET_TYPES)}>
                Ticket Type: {getTicketTypeDisplayName()}
              </Tag>
            )}
          </div>
        )}
      </Card>

      {loading ? (
        <div className="loading-container">
          <Spin size="large" tip="Loading report data..." />
        </div>
      ) : reportData ? (
        <>
          <Row gutter={[16, 16]} className="summary-cards">
            <Col xs={24} sm={12} md={6} lg={4}>
              <Card className="stat-card">
                <Statistic
                  title="Total Closed Tickets"
                  value={reportData.totalTickets || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a', fontSize: 32 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Card className="stat-card">
                <Statistic
                  title="Date Range"
                  value={getDateRangeDisplay()}
                  valueStyle={{ fontSize: 14 }}
                />
                <Text type="secondary">{getMonthCount()} month(s)</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Card className="stat-card">
                <Statistic
                  title="Contract"
                  value={getContractDisplayName()}
                  prefix={<ShoppingOutlined />}
                  valueStyle={{ fontSize: 13 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Card className="stat-card">
                <Statistic
                  title="Ticket Type"
                  value={getTicketTypeDisplayName()}
                  prefix={<AppstoreOutlined />}
                  valueStyle={{ fontSize: 13 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Card className="stat-card">
                <Statistic
                  title="Organization"
                  value={getOrganizationName()}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ fontSize: 12 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Card className="stat-card">
                <Statistic
                  title="Prepared By"
                  value={reportData.preparedBy || `${user?.firstName || ''} ${user?.lastName || ''}`}
                  valueStyle={{ fontSize: 13 }}
                />
              </Card>
            </Col>
          </Row>

          {renderTicketTypeSummary()}

          <Alert
            message="Information"
            description="This report only shows tickets with CLOSED status. Only tickets closed within the selected date range are included."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Card className="report-card">
            <div className="report-actions">
              <Space>
                <Text strong>Total Tickets: </Text>
                <Tag color="green" style={{ fontSize: 16, fontWeight: 'bold' }}>
                  {reportData.totalTickets || 0}
                </Tag>
              </Space>
              <Space>
                <Button 
                  type="primary"
                  icon={<FileWordOutlined />} 
                  onClick={handleGenerateDocument}
                  loading={exportLoading}
                  disabled={!reportData.tickets || reportData.tickets.length === 0}
                  size="large"
                >
                  Generate {selectedFormat.toUpperCase()} Document
                </Button>
              </Space>
            </div>

            <Table
              columns={getTableColumns()}
              dataSource={getTableDataSource()}
              loading={loading}
              pagination={{ 
                pageSize: 10, 
                showSizeChanger: true, 
                showTotal: (total) => `Total ${total} tickets` 
              }}
              bordered
              size="middle"
              scroll={{ x: 1300 }}
              className="report-table"
            />

            <div className="report-footer">
              <Text type="secondary">
                Generated on {dayjs().format('DD MMM YYYY, hh:mm A')} by {user?.firstName} {user?.lastName} ({user?.roleName})
              </Text>
            </div>
          </Card>
        </>
      ) : filtersApplied ? (
        <Card className="empty-card">
          <Empty
            description="No data found for the selected filters. Please try different date range or filter options."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <Card className="empty-card">
          <Empty
            description={
              <span>
                <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                Select date range and click "Apply Filters & Generate Report" to view data.<br />
                <Text type="secondary">Optional: Filter by Contract and/or Ticket Type</Text>
              </span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}
    </div>
  );
};

export default SummaryBorangAduan;